import {Activity, ArrowRight, Coins, PenLine, Send, Star} from 'lucide-react'
import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import ColonyAvatarEditor from '@/components/ColonyAvatarEditor'
import ColonyProfileComposer from '@/components/ColonyProfileComposer'
import ColonyUsernameEditor from '@/components/ColonyUsernameEditor'
import Layout from '@/components/Layout'
import ProjectCard from '@/components/ProjectCard'
import SeoHead from '@/components/SeoHead'
import SimLaunchCard from '@/components/SimLaunchCard'
import StatusBadge from '@/components/StatusBadge'
import { useColonyActivity } from '@/hooks/useColonyActivity'
import { useColonyHoldings } from '@/hooks/useColonyHoldings'
import { useSimLaunchpad } from '@/hooks/useSimLaunchpad'
import { useSimPresales } from '@/hooks/useSimPresales'
import { useWatchlist } from '@/hooks/useWatchlist'
import { colonyProfilePath } from '@/lib/colonyProfile'
import { formatNumber } from '@/lib/format'
import { sameAddress, shortenAddress } from '@/lib/launchpad/contracts'
import { weiToEthString } from '@/lib/launchpad/wei'
import { getProjectById } from '@/lib/mockData'
import { getPresaleStatus, pledgedByWallet } from '@/lib/presale/math'
import { getPresaleProjectById } from '@/lib/presale/listings'
import { simPresaleToProject } from '@/lib/presale/toProject'
import { tokenSharePath } from '@/lib/shareSlug'

const kindLabels: Record<string, string> = {
  buy: 'Buy',
  sell: 'Sell',
  contribute: 'Contribution',
  withdraw: 'Withdrawal',
  submit: 'Submission',
  claim: 'Claim',
}

function MyColony() {
  const { activity, balances, address } = useColonyActivity()
  const { launches, walletWei } = useSimLaunchpad()
  const { presales } = useSimPresales()
  const { holdings: liveHoldings, loading: holdingsLoading } = useColonyHoldings({ address, launches, presales })
  const { ids: watchIds } = useWatchlist()
  const watchedProjects = watchIds
    .map(id => getPresaleProjectById(id) ?? getProjectById(id))
    .filter((project): project is NonNullable<typeof project> => Boolean(project))
  const watchedCurves = useMemo(
    () => launches.filter(launch => watchIds.includes(launch.id)),
    [launches, watchIds],
  )
  const watchCount = watchedProjects.length + watchedCurves.length

  const holdings = useMemo(() => {
    const mockHoldings = Object.entries(balances)
      .filter(([, amount]) => amount > 0)
      .map(([projectId, amount]) => {
        const project = getProjectById(projectId)
        return {
          projectId,
          amountLabel: `${formatNumber(amount)} ${project?.ticker ?? 'TOKEN'}`,
          name: project?.name ?? projectId,
          ticker: project?.ticker ?? 'TOKEN',
          href: tokenSharePath(project?.shareSlug, projectId),
          note: project?.type === 'presale' ? 'Presale' : 'Legacy curve',
        }
      })

    const rows = new Map<string, (typeof liveHoldings)[number]>()
    for (const row of [...liveHoldings, ...mockHoldings]) {
      if (!rows.has(row.projectId))
        rows.set(row.projectId, row)
    }
    return [...rows.values()]
  }, [balances, liveHoldings])

  const createdLaunches = useMemo(
    () => launches.filter(launch => sameAddress(launch.creatorAddress, address)),
    [address, launches],
  )

  const createdPresales = useMemo(
    () => presales.filter(presale => sameAddress(presale.creatorAddress, address)),
    [address, presales],
  )

  const pledgedPresales = useMemo(
    () => presales
      .map(presale => {
        const pledgedWei = pledgedByWallet(presale, address)
        return {
          presale,
          pledgedWei,
          status: getPresaleStatus(presale),
        }
      })
      .filter(item => item.pledgedWei > 0n),
    [address, presales],
  )

  const pledgedWeiTotal = useMemo(
    () => pledgedPresales.reduce((sum, item) => sum + item.pledgedWei, 0n),
    [pledgedPresales],
  )

  const stats = useMemo(() => {
    const trades = activity.filter(item => item.kind === 'buy' || item.kind === 'sell').length
    return {
      trades,
      pledgedEth: weiToEthString(pledgedWeiTotal, 4),
      holdingCount: holdings.length,
    }
  }, [activity, holdings.length, pledgedWeiTotal])

  return (
    <Layout>
      <SeoHead
        title="My Colony — Wallet Activity"
        description="Review Penguin Pad trades, presale contributions, and project submissions for the connected wallet."
      />

      <section>
        <div className="mx-auto max-w-7xl px-4 pb-2 pt-12 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4">
            <ColonyAvatarEditor />
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-lime-400">Portfolio</p>
              <h1 className="text-3xl font-bold text-white sm:text-4xl">My Colony</h1>
              <p className="text-sm text-zinc-400">
                {address
                  ? `Wallet ${shortenAddress(address)} is the account · ${weiToEthString(walletWei, 5)} ETH local quote cache`
                  : 'Connect a wallet to load this address’s holdings, watchlist, and activity — no email login'}
              </p>
              {address
                ? (
                    <>
                      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                        <p className="text-zinc-500">Set a username and photo. Public Colony pages never show your wallet.</p>
                        <Link to={colonyProfilePath(address)} className="font-medium text-lime-400 hover:text-lime-300">
                          View public profile
                        </Link>
                        <Link to="/explore-colony" className="font-medium text-zinc-300 hover:text-white">
                          Explore the Colony
                        </Link>
                        <Link to="/colony-newsfeed" className="font-medium text-zinc-300 hover:text-white">
                          Colony Newsfeed
                        </Link>
                      </div>
                      <ColonyUsernameEditor />
                    </>
                  )
                : (
                    <Link to="/explore-colony" className="mt-2 inline-flex text-xs font-medium text-lime-400 hover:text-lime-300">
                      Explore the Colony
                    </Link>
                  )}
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        {address
          ? (
              <div className="mb-10 rounded-3xl border border-lime-400/25 bg-lime-400/[0.06] p-5 backdrop-blur-md sm:p-6">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-lime-400">
                      <PenLine className="h-3.5 w-3.5" aria-hidden="true" />
                      Create a post
                    </p>
                    <h2 className="mt-1 text-lg font-semibold text-white">Share a note or photo</h2>
                    <p className="mt-1 text-xs text-zinc-400">
                      Posts go on your public Colony profile and the Colony Newsfeed. You can attach an official penguinpad.xyz project link.
                    </p>
                  </div>
                  <Link
                    to={colonyProfilePath(address)}
                    className="inline-flex shrink-0 items-center gap-1 rounded-full border border-white/15 px-3 py-1.5 text-xs font-semibold text-zinc-200 hover:border-lime-400/40 hover:text-white"
                  >
                    View public profile
                    <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                  </Link>
                </div>
                <div className="mt-4">
                  <ColonyProfileComposer />
                </div>
              </div>
            )
          : null}

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-5 backdrop-blur-md">
            <p className="text-xs uppercase tracking-[0.14em] text-zinc-500">Projects held</p>
            <p className="mt-1 text-2xl font-bold text-lime-400">{holdingsLoading && holdings.length === 0 ? '…' : stats.holdingCount}</p>
            <p className="mt-1 text-[11px] text-zinc-500">Different Penguin Pad projects this wallet holds</p>
          </div>
          <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-5 backdrop-blur-md">
            <p className="text-xs uppercase tracking-[0.14em] text-zinc-500">Trades</p>
            <p className="mt-1 text-2xl font-bold text-white">{stats.trades}</p>
          </div>
          <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-5 backdrop-blur-md">
            <p className="text-xs uppercase tracking-[0.14em] text-zinc-500">Presale pledged</p>
            <p className="mt-1 text-2xl font-bold text-orange-400">{stats.pledgedEth} ETH</p>
          </div>
          <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-5 backdrop-blur-md">
            <p className="text-xs uppercase tracking-[0.14em] text-zinc-500">Watchlist</p>
            <p className="mt-1 text-2xl font-bold text-white">{watchCount}</p>
          </div>
        </div>

        <div className="mt-10">
          <div className="flex items-center gap-2">
            <Star className="h-4 w-4 text-lime-400" aria-hidden="true" />
            <h2 className="text-sm font-semibold text-white">Watched curves</h2>
          </div>
          {watchedCurves.length > 0
            ? (
                <div className="mt-4 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                  {watchedCurves.map(launch => (
                    <SimLaunchCard key={launch.id} launch={launch} />
                  ))}
                </div>
              )
            : (
                <div className="mt-4 rounded-2xl border border-dashed border-white/15 px-4 py-8 text-center">
                  <Star className="mx-auto h-6 w-6 text-zinc-600" aria-hidden="true" />
                  <p className="mt-3 text-sm text-zinc-400">
                    {address ? 'Star a curve on Launchpad to pin it here.' : 'Connect a wallet to save watched curves to that address.'}
                  </p>
                </div>
              )}
        </div>

        <div className="mt-10">
          <div className="flex items-center gap-2">
            <Star className="h-4 w-4 text-orange-400" aria-hidden="true" />
            <h2 className="text-sm font-semibold text-white">Watched Presales</h2>
          </div>
          {watchedProjects.length > 0
            ? (
                <div className="mt-4 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                  {watchedProjects.map(project => (
                    <ProjectCard key={project.id} project={project} />
                  ))}
                </div>
              )
            : (
                <div className="mt-4 rounded-2xl border border-dashed border-white/15 px-4 py-8 text-center">
                  <p className="text-sm text-zinc-400">
                    {address ? 'Star a presale to pin it here.' : 'Connect a wallet to save watched presales to that address.'}
                  </p>
                </div>
              )}
        </div>

        <div className="mt-10">
          <h2 className="text-sm font-semibold text-white">Created launches</h2>
          <p className="mt-1 text-xs text-zinc-500">Quick launches this wallet opened. Curve creator fees are disbursed at bonding; Uniswap fees are disbursed on every swap.</p>
          {createdLaunches.length > 0
            ? (
                <div className="mt-4 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                  {createdLaunches.map(launch => (
                    <SimLaunchCard key={launch.id} launch={launch} />
                  ))}
                </div>
              )
            : (
                <div className="mt-4 rounded-2xl border border-dashed border-white/15 px-4 py-8 text-center">
                  <p className="text-sm text-zinc-400">
                    {address ? 'No quick launches created from this wallet yet.' : 'Connect a wallet to see launches created by that address.'}
                  </p>
                  <Link to="/submit?type=launchpad" className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-lime-400">
                    Open a launch
                    <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                  </Link>
                </div>
              )}
        </div>

        <div className="mt-10">
          <h2 className="text-sm font-semibold text-white">Created raises</h2>
          <p className="mt-1 text-xs text-zinc-500">ETH presales this wallet opened. Opening a raise costs 0.1 ETH. Raise proceeds pay at finalize; Uniswap creator fees are disbursed on every swap.</p>
          {createdPresales.length > 0
            ? (
                <div className="mt-4 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                  {createdPresales.map(presale => (
                    <ProjectCard key={presale.id} project={simPresaleToProject(presale)} />
                  ))}
                </div>
              )
            : (
                <div className="mt-4 rounded-2xl border border-dashed border-white/15 px-4 py-8 text-center">
                  <p className="text-sm text-zinc-400">
                    {address ? 'No ETH raises created from this wallet yet.' : 'Connect a wallet to see raises created by that address.'}
                  </p>
                  <Link to="/submit" className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-orange-400">
                    Open a presale
                    <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                  </Link>
                </div>
              )}
        </div>

        <div className="mt-10">
          <h2 className="text-sm font-semibold text-white">Presale positions</h2>
          <p className="mt-1 text-xs text-zinc-500">Live pledged ETH by raise. Contribute during the start/end window; withdraw until close or after cancel.</p>
          {pledgedPresales.length > 0
            ? (
                <div className="mt-4 space-y-3">
                  {pledgedPresales.map(({ presale, pledgedWei, status }) => (
                    <Link
                      key={presale.id}
                      to={tokenSharePath(presale.shareSlug, presale.id)}
                      className="flex items-center justify-between gap-3 rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3 backdrop-blur-md transition-colors hover:border-orange-400/30"
                    >
                      <div className="flex min-w-0 items-center gap-3">
                        {presale.imageUrl
                          ? (
                              <img
                                src={presale.imageUrl}
                                alt={`${presale.name} token art`}
                                className="h-10 w-10 shrink-0 rounded-xl object-cover"
                              />
                            )
                          : (
                              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-orange-400/30 bg-orange-400/10 text-xs font-bold text-orange-400">
                                {presale.ticker.slice(0, 2)}
                              </div>
                            )}
                        <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-white">{presale.name}</p>
                        <p className="text-xs text-zinc-500">
                          $
                          {presale.ticker}
                          {' '}
                          ·
                          {' '}
                          {weiToEthString(presale.raisedWei, 3)}
                          {' '}
                          /
                          {' '}
                          {weiToEthString(presale.hardCapWei, 3)}
                          {' '}
                          ETH raised
                        </p>
                        </div>
                      </div>
                      <div className="flex shrink-0 items-center gap-3">
                        <StatusBadge status={status} />
                        <p className="text-sm font-semibold text-orange-400">
                          {weiToEthString(pledgedWei, 4)}
                          {' '}
                          ETH
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              )
            : (
                <div className="mt-4 rounded-2xl border border-dashed border-white/15 px-4 py-8 text-center">
                  <p className="text-sm text-zinc-400">
                    {address ? 'No pledged ETH on this wallet yet.' : 'Connect a wallet to load pledged ETH for that address.'}
                  </p>
                  <Link to="/presale" className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-orange-400">
                    Browse raises
                    <Send className="h-3.5 w-3.5" aria-hidden="true" />
                  </Link>
                </div>
              )}
        </div>

        <div className="mt-8 grid gap-8 lg:grid-cols-5">
          <div className="lg:col-span-2">
            <h2 className="text-sm font-semibold text-white">Holdings</h2>
            {holdings.length > 0
              ? (
                  <div className="mt-4 space-y-3">
                    {holdings.map(item => (
                      <Link
                        key={item.projectId}
                        to={item.href}
                        className="flex items-center justify-between rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3 backdrop-blur-md transition-colors hover:border-lime-400/30"
                      >
                        <div>
                          <p className="text-sm font-medium text-white">{item.name}</p>
                          <p className="text-xs text-zinc-500">
                            $
                            {item.ticker}
                            {' '}
                            ·
                            {' '}
                            {item.note}
                          </p>
                        </div>
                        <p className="text-sm font-semibold text-lime-400">{item.amountLabel}</p>
                      </Link>
                    ))}
                  </div>
                )
              : (
                  <div className="mt-4 rounded-2xl border border-dashed border-white/15 px-4 py-8 text-center">
                    <Coins className="mx-auto h-6 w-6 text-zinc-600" aria-hidden="true" />
                    <p className="mt-3 text-sm text-zinc-400">
                      {address
                        ? (holdingsLoading ? 'Checking on-chain holdings…' : 'No token holdings for this wallet yet.')
                        : 'Connect a wallet to see curve holdings for that address.'}
                    </p>
                    <Link to="/launchpad" className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-lime-400">
                      Trade a launch
                      <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                    </Link>
                  </div>
                )}
          </div>

          <div className="lg:col-span-3">
            <h2 className="text-sm font-semibold text-white">Activity</h2>
            {activity.length > 0
              ? (
                  <ol className="mt-4 space-y-3">
                    {activity.map(item => (
                      <li key={item.id} className="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3 backdrop-blur-md">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="text-sm font-medium text-white">{item.summary}</p>
                            <p className="mt-1 text-xs text-zinc-500">
                              {kindLabels[item.kind] ?? item.kind}
                              {' '}
                              ·
                              {' '}
                              {item.projectName}
                              {' '}
                              ($
                              {item.ticker}
                              )
                            </p>
                          </div>
                          <span className="shrink-0 text-[11px] text-zinc-600">
                            {new Date(item.createdAt).toLocaleString()}
                          </span>
                        </div>
                      </li>
                    ))}
                  </ol>
                )
              : (
                  <div className="mt-4 rounded-2xl border border-dashed border-white/15 px-4 py-10 text-center">
                    <Activity className="mx-auto h-6 w-6 text-zinc-600" aria-hidden="true" />
                    <p className="mt-3 text-sm text-zinc-400">
                      {address ? 'No activity for this wallet yet.' : 'Connect a wallet to load activity for that address.'}
                    </p>
                    <div className="mt-4 flex flex-wrap items-center justify-center gap-3">
                      <Link to="/launchpad" className="inline-flex items-center gap-1 text-sm font-medium text-lime-400">
                        Browse launchpad
                        <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                      </Link>
                      <Link to="/presale" className="inline-flex items-center gap-1 text-sm font-medium text-orange-400">
                        Join a raise
                        <Send className="h-3.5 w-3.5" aria-hidden="true" />
                      </Link>
                    </div>
                  </div>
                )}
          </div>
        </div>
      </section>
    </Layout>
  )
}

export default MyColony
